import React, { useState } from 'react';
import { Card } from '@components/atoms/Card';
import { Button } from '@components/atoms/Button';
import Alert from '@components/atoms/Alert';
import {
  OrganizationSubscription,
  OrganizationSubscriptionStatus,
  PaymentMethod,
} from '@models/subscription';
import {
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  DollarSign,
  PlayCircle,
  Users,
} from 'lucide-react';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { buildFreeRolesNote } from '@constants/seatCountingRoles';
import { useTranslation } from '@hooks/useTranslation';
import { getSubscriptionStatusConfig } from '@utils/subscriptionStatusConfig';

interface SubscriptionDetailsProps {
  subscription: OrganizationSubscription | undefined | null;
  paymentMethods?: PaymentMethod[];
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onStartSubscription?: () => void;
  onAddPaymentMethodForStart?: () => void;
  /** Convert a lapsed trial into a paying subscription using the saved card. */
  onActivateAfterTrial?: () => Promise<void> | void;
  isActivatingAfterTrial?: boolean;
  isLoading?: boolean;
  isStartingSubscription?: boolean;
  /** Org-wide included seats (base + addons). */
  totalSeats?: number;
  /** Active users currently occupying a seat. */
  activeSeatsCount?: number;
}

const SubscriptionDetails: React.FC<SubscriptionDetailsProps> = ({
  subscription,
  paymentMethods = [],
  onPause,
  onResume,
  onCancel,
  onStartSubscription,
  onAddPaymentMethodForStart,
  onActivateAfterTrial,
  isActivatingAfterTrial = false,
  isLoading = false,
  isStartingSubscription = false,
  totalSeats,
  activeSeatsCount,
}) => {
  const [showActivateModal, setShowActivateModal] = useState(false);
  const t = useTranslation('billing');
  const tEnumLabels = useTranslation('enumLabels');

  if (!subscription) {
    return (
      <Card>
        <Alert variant="info" showIcon title={t.subscription.noActiveSubscriptionTitle}>
          <p>
            {t.subscription.noActiveSubscriptionBody}
          </p>
        </Alert>
      </Card>
    );
  }

  const statusConfig = getSubscriptionStatusConfig(
    subscription.status,
    tEnumLabels,
  );
  const StatusIcon = statusConfig.icon;

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatAmount = (amountInCents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amountInCents / 100);
  };

  /**
   * A lapsed trial that already has a card on file is one click from being unblocked —
   * but nothing was making that click available. `addPaymentMethod` only vaults the card;
   * the status change lives in `activateSubscriptionAfterTrial`, which until now was
   * reachable only from `/trial-expired`, a page that asks for the card details all over
   * again. This is that missing step, using the card the system already holds.
   */
  const needsActivationAfterTrial =
    subscription.status === OrganizationSubscriptionStatus.TRIAL_EXPIRED &&
    paymentMethods.length > 0;

  const showPastDueAlert =
    subscription.status === OrganizationSubscriptionStatus.PAST_DUE;
  const showTrialAlert =
    subscription.status === OrganizationSubscriptionStatus.TRIAL;
  const canResume =
    subscription.status === OrganizationSubscriptionStatus.PAUSED;
  const canCancel = [
    OrganizationSubscriptionStatus.ACTIVE,
    OrganizationSubscriptionStatus.PAUSED,
    OrganizationSubscriptionStatus.TRIAL,
  ].includes(subscription.status);

  // Check if subscription needs to be started (admin-assigned plan without NMI billing)
  const needsToStartBilling =
    !subscription.nmiSubscriptionId &&
    subscription.status === OrganizationSubscriptionStatus.ACTIVE;

  const hasPaymentMethod = paymentMethods.length > 0;

  const defaultCard =
    paymentMethods.find((method) => method.isDefault) || paymentMethods[0];
  const planPriceLabel = formatAmount(
    subscription.planSnapshot?.basePriceCents || 0,
  );
  const billingPeriodLabel =
    subscription.billingPeriod === 'ANNUAL' ? 'year' : 'month';

  return (
    <div className="space-y-4">
      {needsActivationAfterTrial && (
        <Alert
          variant="error"
          showIcon
          title={t.subscription.activateOneStepTitle}>
          <p>
            {t.subscription.cardEndingPrefix} <strong>{defaultCard?.last4}</strong>{' '}
            {t.subscription.cardEndingMiddle} <strong>{t.subscription.stillReadOnlyEmphasis}</strong>
            {t.subscription.cardSavedTail}
          </p>
          <Button
            variant="primary"
            size="md"
            className="mt-3"
            loading={isActivatingAfterTrial}
            disabled={isActivatingAfterTrial}
            onClick={() => setShowActivateModal(true)}>
            {t.subscription.activateSubscriptionButton}
          </Button>
        </Alert>
      )}

      <ConfirmationModal
        isOpen={showActivateModal}
        variant="info"
        title={t.subscription.activateModalTitle}
        description={t.subscription.activateModalDescription
          .replace('{last4}', defaultCard?.last4 ?? '')
          .replace('{price}', planPriceLabel)
          .replace(
            '{period}',
            billingPeriodLabel === 'year' ? t.subscription.periodAnnual : t.subscription.periodMonthly,
          )}
        confirmText={t.subscription.activateAndPayButton}
        cancelText={t.subscription.notNowButton}
        onClose={() => setShowActivateModal(false)}
        onConfirm={async () => {
          setShowActivateModal(false);
          await onActivateAfterTrial?.();
        }}
      />

      {showPastDueAlert && (
        <Alert variant="error" showIcon title={t.subscription.paymentFailedTitle}>
          <p>
            {t.subscription.paymentFailedBody}
          </p>
        </Alert>
      )}

      {showTrialAlert && subscription.trialEndsAt && (
        <Alert variant="info" showIcon title={t.subscription.trialPeriodActiveTitle}>
          <p>
            {t.subscription.trialEndsBodyPrefix} {formatDate(subscription.trialEndsAt)}.{' '}
            {t.subscription.trialEndsBodySuffix}
          </p>
        </Alert>
      )}

      {needsToStartBilling && (
        <Alert variant="warning" showIcon title={t.subscription.billingNotActiveTitle}>
          <p>
            {t.subscription.billingNotActiveBodyPrefix}{' '}
            {hasPaymentMethod
              ? t.subscription.billingNotActiveWithCard
              : t.subscription.billingNotActiveWithoutCard}
          </p>
        </Alert>
      )}

      <Card>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t.subscription.detailsTitle}
          </h2>
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${statusConfig.className}`}>
            <StatusIcon className="w-4 h-4" />
            <span className="text-sm font-medium">{statusConfig.text}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.subscription.planAmountLabel}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {subscription.planName || subscription.planSnapshot?.name} -{' '}
                  {formatAmount(subscription.planSnapshot?.basePriceCents || 0)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                  {t.subscription.billedPrefix}{' '}
                  {subscription.billingPeriod === 'ANNUAL'
                    ? t.subscription.periodAnnual
                    : t.subscription.periodMonthly}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Calendar className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.subscription.startDateLabel}
                </p>
                <p className="text-base font-medium text-gray-900 dark:text-white">
                  {formatDate(subscription.startedAt)}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {subscription.trialEndsAt &&
              subscription.status === OrganizationSubscriptionStatus.TRIAL && (
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t.subscription.trialEndsLabel}
                    </p>
                    <p className="text-base font-medium text-gray-900 dark:text-white">
                      {formatDate(subscription.trialEndsAt)}
                    </p>
                  </div>
                </div>
              )}

            {subscription.canceledAt && (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t.subscription.canceledAtLabel}
                  </p>
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    {formatDate(subscription.canceledAt)}
                  </p>
                </div>
              </div>
            )}

            {typeof totalSeats === 'number' && (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t.subscription.seatsLabel}
                  </p>
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    {t.subscription.seatsUsedSuffix
                      .replace('{active}', String(activeSeatsCount ?? 0))
                      .replace('{total}', String(totalSeats))}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Full-width seats availability message */}
        {typeof totalSeats === 'number' && (
          <div className="mt-4 rounded-lg bg-gray-50 dark:bg-gray-800/40 px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
            <p className="m-0">
              {Math.max(totalSeats - (activeSeatsCount ?? 0), 0) > 0
                ? `${t.subscription.seatsAvailablePrefix
                    .replace('{active}', String(activeSeatsCount ?? 0))
                    .replace('{total}', String(totalSeats))} ${Math.max(
                    totalSeats - (activeSeatsCount ?? 0),
                    0,
                  )} ${t.subscription.seatsAvailableSuffix}`
                : t.subscription.seatsAllUsedMessage.replace('{total}', String(totalSeats))}
            </p>
            {buildFreeRolesNote(
              t.seats,
              subscription?.planSnapshot?.seatCountingRoles,
            ) && (
              <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400">
                {buildFreeRolesNote(
                  t.seats,
                  subscription?.planSnapshot?.seatCountingRoles,
                )}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
          {/* Start Billing button - shown when plan is assigned but no NMI billing */}
          {needsToStartBilling && hasPaymentMethod && onStartSubscription && (
            <Button
              variant="primary"
              leftIcon={<PlayCircle className="w-4 h-4" />}
              onClick={onStartSubscription}
              disabled={isLoading || isStartingSubscription}
              loading={isStartingSubscription}>
              {t.subscription.startBillingButton}
            </Button>
          )}

          {/* Add Payment Method button - shown when plan is assigned but no payment method */}
          {needsToStartBilling &&
            !hasPaymentMethod &&
            onAddPaymentMethodForStart && (
              <Button
                variant="primary"
                leftIcon={<PlayCircle className="w-4 h-4" />}
                onClick={onAddPaymentMethodForStart}
                disabled={isLoading}>
                {t.subscription.addPaymentMethodToStartButton}
              </Button>
            )}

          {/* {canPause && (
            <Button
              variant="secondary"
              leftIcon={<Pause className="w-4 h-4" />}
              onClick={onPause}
              disabled={isLoading}
              loading={isLoading}
            >
              Pause Subscription
            </Button>
          )} */}

          {canResume && (
            <Button
              variant="primary"
              leftIcon={<CheckCircle className="w-4 h-4" />}
              onClick={onResume}
              disabled={isLoading}
              loading={isLoading}>
              {t.subscription.resumeSubscriptionButton}
            </Button>
          )}

          {canCancel && !needsToStartBilling && (
            <Button
              variant="tertiary"
              leftIcon={<XCircle className="w-4 h-4" />}
              onClick={onCancel}
              disabled={isLoading}
              className="ml-auto text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
              {t.subscription.cancelSubscriptionButton}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SubscriptionDetails;
